import { ChartManager } from './chart.manager';

import { isHorizontalType, isPieType, isTimeType, isVerticalType } from '@23degrees/shared-code/config/constants';
import { D3LineData, d3renderCircle, d3renderLine, d3renderPath } from './chart.d3';

import chroma from 'chroma-js';
import { roundTo } from '@23degrees/shared-code/utilities';
import { DotTypes } from '@23degrees/shared-code/config/constants';
import { Quill } from '@23degrees/shared-code/domain/content/entities/valueobjects';
import { DashType, strokeDasharrayFormat } from './utilities/utils';

// hex transitions suck, alpha would need svg fill #rrggbb and fill-opacity #a
const color2css = (color) => chroma(color).hex();

export const renderGrid = (chartManager: ChartManager): void => {
  const { type, typeSpecific } = chartManager.content;
  const { grid, gridZero, gridUser, dynamicHeight, dots, axis } = typeSpecific.chart;
  const { gridLines, customQuills } = axis.labelAxis;
  const { valueAxis } = typeSpecific.chart.axis;

  const data: D3LineData[] = [];
  const dataFG: D3LineData[] = [];

  if (isPieType(type)) {
    d3renderLine(chartManager.chart.element, data, '.grid');
    d3renderLine(chartManager.chart.element, dataFG, '.grid-foreground');
    return;
  }

  d3renderPath(chartManager.chart.element, [], '.grid-foreground');
  d3renderCircle(chartManager.chart.element, [], '.grid-foreground');

  // horizontal lines
  if (isVerticalType(type) || isTimeType(type)) {
    const ticks = chartManager.yScale.ticks(Number.parseInt(String(grid.yAxis), 10));
    for (const obj of ticks) {
      const y = roundTo(chartManager.yScale(obj), 1);
      if (grid.enabled && valueAxis.enabled) {
        if (!gridZero.enabled || obj !== 0) {
          const tickLine: D3LineData = {
            x1: 0,
            x2: chartManager.chartWidth,
            y1: y,
            y2: y,
            color: color2css(grid.color),
            width: grid.width,
            lineDash: grid.lineDash,
          };

          data.push(tickLine);
        }
      }

      if (gridZero.enabled && obj === 0) {
        const zeroLine: D3LineData = {
          x1: 0,
          x2: chartManager.chartWidth,
          y1: y,
          y2: y,
          color: color2css(gridZero.color),
          width: gridZero.width,
          lineDash: gridZero.lineDash,
        };

        dataFG.push(zeroLine);
      }
    }

    if (gridUser.enabled) {
      for (const obj of gridUser.values) {
        const y = chartManager.yScale(obj);
        const userLine: D3LineData = {
          x1: 0,
          x2: chartManager.chartWidth,
          y1: y,
          y2: y,
          color: color2css(gridUser.color),
          width: gridUser.width,
          lineDash: gridUser.lineDash,
        };

        if (gridUser.isAbove) {
          dataFG.push(userLine);
        } else {
          data.push(userLine);
        }
      }
    }

    d3renderLine(chartManager.chart.element, data, '.grid');
    d3renderLine(chartManager.chart.element, dataFG, '.grid-foreground');
  }

  // vertical lines
  if (isHorizontalType(type)) {
    const ticks = chartManager.xScale.ticks(Number.parseInt(String(grid.xAxis), 10));
    const topOffset = chartManager.getOffset('top');
    const bottomOffset = chartManager.getOffset('bottom');

    const y1 = DotTypes.includes(type) && dots.firstLabel.enabled ? chartManager.dotFirstLabelHeight + chartManager.dotFirstLabelOffset : 0;
    const y2 = chartManager.totalHeight - chartManager.xAxisHeight - topOffset - bottomOffset;

    for (const obj of ticks) {
      const x = roundTo(chartManager.xScale(obj), 1);
      if (grid.enabled && valueAxis.enabled) {
        if (!gridZero.enabled || obj !== 0) {
          const tickLine: D3LineData = {
            x1: x,
            x2: x,
            y1: y1,
            y2: y2,
            color: color2css(grid.color),
            width: grid.width,
            lineDash: grid.lineDash,
          };

          data.push(tickLine);
        }
      }

      if (gridZero.enabled && obj === 0) {
        const zeroLine: D3LineData = {
          x1: x,
          x2: x,
          y1: y1,
          y2: y2,
          color: color2css(gridZero.color),
          width: gridZero.width,
          lineDash: gridZero.lineDash,
        };

        dataFG.push(zeroLine);
      }
    }

    if (gridUser.enabled) {
      for (const obj of gridUser.values) {
        const x = chartManager.xScale(obj);
        const userLine: D3LineData = {
          x1: x,
          x2: x,
          y1: y1,
          y2: y2,
          color: color2css(gridUser.color),
          width: gridUser.width,
          lineDash: gridUser.lineDash,
        };

        if (gridUser.isAbove) {
          dataFG.push(userLine);
        } else {
          data.push(userLine);
        }
      }
    }

    d3renderLine(chartManager.chart.element, data, '.grid');
    d3renderLine(chartManager.chart.element, dataFG, '.grid-foreground');
  }

  // horizontal lines dot plot charts
  if (DotTypes.includes(type)) {
    const bandWidth = chartManager.yScaleBandWidth();

    for (const [idx, obj] of chartManager.yDomain.entries()) {

      const { item } = chartManager.yScaleOrdinal(obj);
      const height = item.height ?? 30; // bars.height
      const y1 = item.y + height / 2;
      const y2 = item.y + height / 2;

      let color = color2css(gridLines.stroke);
      let width = gridLines.strokeWidth;
      let lineDash = strokeDasharrayFormat(gridLines.strokeDasharray as DashType);

      const customQuill = customQuills.find((quill:Quill) => quill.id === obj);
      if(customQuill) {
        color = customQuill.lineStyles.stroke;
        width = customQuill.lineStyles.strokeWidth;
        lineDash = strokeDasharrayFormat(customQuill.lineStyles.strokeDasharray);
      }

      const dotLine: D3LineData = {
        x1: 0,
        x2: chartManager.chartWidth,
        y1: y1,
        y2: y2,

        color,
        width,
        lineDash,
      };

      if(customQuill?.enabled) {
        data.push(dotLine);
      } else {
        if(!customQuill && gridLines.enabled) data.push(dotLine);
      }
    }

    d3renderLine(chartManager.chart.element, data, '.grid');
    d3renderLine(chartManager.chart.element, dataFG, '.grid-foreground');
  }
};
